import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faEnvelope, 
  faPhone, 
  faTrash,
  faCheckCircle,
  faClock,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { BookingRequest } from '../../types/booking';
import { slideIn } from './AdminStyles';

// Styles du tableau
const BookingsSection = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #f3f4f6;
  overflow: hidden;
  animation: ${slideIn} 0.6s ease-out 0.3s both;
`;

const SectionHeader = styled.div`
  background: linear-gradient(135deg, #1f2937, #374151);
  color: white;
  padding: 2rem;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    
    h2 {
      font-size: 1.25rem;
    }
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  
  @media (max-width: 768px) {
    margin: -1px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
  
  th {
    padding: 1.5rem 1rem;
    text-align: left;
    font-weight: 700;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
  }
  
  @media (max-width: 768px) {
    th {
      padding: 1rem 0.5rem;
      font-size: 0.7rem;
    }
  }
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const TableCell = styled.td`
  padding: 1.5rem 1rem;
  vertical-align: top;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const ClientInfo = styled.div`
  .name {
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }
  
  .contact {
    color: #6b7280;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }
  
  @media (max-width: 768px) {
    .name {
      font-size: 0.875rem;
    }
    
    .contact {
      font-size: 0.75rem;
    }
  }
`;

const ServiceBadge = styled.span`
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  
  ${props => {
    switch (props.$status) {
      case 'pending':
        return `
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
        `;
      case 'confirmed':
        return `
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        `;
      case 'cancelled':
        return `
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
        `;
      case 'completed':
        return `
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: white;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
        `;
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const StatusSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:hover {
    border-color: #9ca3af;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  
  &:hover {
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

interface BookingsTableProps {
  bookings: BookingRequest[];
  onStatusChange: (bookingId: number, newStatus: string) => void;
  onDelete: (bookingId: number) => void;
}

const getStatusLabel = (status: string) => {
  const labels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    cancelled: 'Annulée',
    completed: 'Terminée',
  };
  return labels[status as keyof typeof labels] || status;
};

export function BookingsTable({ bookings, onStatusChange, onDelete }: BookingsTableProps) {
  if (bookings.length === 0) {
    return (
      <BookingsSection>
        <SectionHeader>
          <h2><FontAwesomeIcon icon={faCalendarAlt} /> Liste des Réservations</h2>
        </SectionHeader>
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#6b7280' }}>
          <div style={{ fontSize: '4rem', color: '#d1d5db', marginBottom: '1.5rem' }}>
            <FontAwesomeIcon icon={faCalendarAlt} />
          </div>
          <h3 style={{ fontSize: '1.25rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>
            Aucune réservation
          </h3>
          <p>Aucune réservation n'a été trouvée pour le moment.</p>
        </div>
      </BookingsSection>
    );
  }

  return (
    <BookingsSection>
      <SectionHeader>
        <h2><FontAwesomeIcon icon={faCalendarAlt} /> Liste des Réservations</h2>
      </SectionHeader>
      
      <TableContainer>
        <Table>
          <TableHeader>
            <tr>
              <th>Client</th>
              <th>Service</th>
              <th>Date & Heure</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </TableHeader>
          <tbody>
            {bookings.map((booking) => (
              <TableRow key={booking.id}>
                <TableCell>
                  <ClientInfo>
                    <div className="name">{booking.name}</div>
                    <div className="contact">
                      <FontAwesomeIcon icon={faEnvelope} /> {booking.email}
                    </div>
                    <div className="contact">
                      <FontAwesomeIcon icon={faPhone} /> {booking.phone}
                    </div>
                  </ClientInfo>
                </TableCell>
                <TableCell>
                  <ServiceBadge>{booking.service_type}</ServiceBadge>
                </TableCell>
                <TableCell>
                  {new Date(booking.date_time).toLocaleString('fr-FR', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </TableCell>
                <TableCell>
                  <StatusBadge $status={booking.status || 'pending'}>
                    {booking.status === 'pending' && <FontAwesomeIcon icon={faClock} />}
                    {booking.status === 'confirmed' && <FontAwesomeIcon icon={faCheckCircle} />}
                    {booking.status === 'cancelled' && <FontAwesomeIcon icon={faTimes} />}
                    {booking.status === 'completed' && <FontAwesomeIcon icon={faCheckCircle} />}
                    {getStatusLabel(booking.status || 'pending')}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <ActionButtons>
                    <StatusSelect
                      value={booking.status || 'pending'}
                      onChange={(e) => onStatusChange(booking.id, e.target.value)}
                    >
                      <option value="pending">En attente</option>
                      <option value="confirmed">Confirmée</option>
                      <option value="completed">Terminée</option>
                      <option value="cancelled">Annulée</option>
                    </StatusSelect>
                    <ActionButton onClick={() => onDelete(booking.id)}>
                      <FontAwesomeIcon icon={faTrash} /> Supprimer
                    </ActionButton>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </BookingsSection>
  );
}
